import axios from "axios"; // tslint:disable-line match-default-export-name

import { gGateway } from "@utils/gateway";

import { AuthenticatedUserModel } from "@courses-utils/gateway-auth";
import { EntityStore } from "@use/entity-store";

import { urls } from "@courses-pm2/api/src/ts/config";
import { ContentModel, ContentModelData } from "@courses-pm2/api/src/ts/models";
import { parseXml } from "@courses-pm2/api/src/ts/parser";
import { Engine, getRequestHeaders } from "@courses-pm2/api/src/ts/utils";

/**
 * Internal cache that'll keep element data in memory for up to 30 minutes to
 * prevent fetching the same data over and over again.
 */
const cache = new EntityStore<ContentModelData>({ tti: 1800 });

/**
 * Helper that'll reach out to the remote backend, and fetch the content of a
 * single element.
 */
export async function getElement({
  user,
  portalId,
  elementId,
  previewMode,
  engine,
}: {
  portalId: string;
  elementId: string;
  user?: AuthenticatedUserModel;
  previewMode?: boolean;
  engine?: Engine;
}): Promise<ContentModel> {
  const cacheId = `${portalId}-${elementId}-${user ? user.id : "guest"}`;

  if (!cache.has(cacheId)) {
    // Fetch the content for the given element
    const response = await axios.get<string>(
      gGateway(
        `${urls.pm2}/v1/portals/${portalId}/contentElements/${elementId}`,
      ),
      {
        headers: await getRequestHeaders({ user, portalId, previewMode }),
      },
    );

    // Now parse the content into ContentModelData
    const model = await parseXml({
      portalId,
      user,
      xml: `<Page>${response.data}</Page>`,
      engine,
    });

    // Finally cache the fetched data
    cache.set(cacheId, model);
  }

  // Extract data
  const data = <ContentModelData>cache.get(cacheId);

  // Turn the data into a well-formed model
  return new ContentModel({ ...data, engine });
}
